/**
 * SharingRegistry — the `/partner` → Sharing sub-page's flat consent ledger:
 * the standard list-page filter bar, the banners (paused + needs-review, the
 * latter carrying the queue's "Review all" entry), and one row per share
 * unit, each row carrying one of four states (`Full` / `Basic` / `Off` /
 * `Review`).
 *
 * It holds **no ledger state of its own**. The ledger object belongs to
 * `PartnerSection`; this class reads it and mutates it through three narrow
 * methods — {@link SharingRegistry#setUnitState}, {@link SharingRegistry#approveUnit},
 * {@link SharingRegistry#submitGroup} — which are the consent API's seams.
 *
 * Each seam has two shapes, and the difference is deliberate:
 *   - **`mockMode`** (the `?mockState=` fixture harness): mutate the local
 *     ledger and return `null` — synchronous, and provably zero network.
 *   - **real data**: call the injected `api` callback and return its promise,
 *     resolving only once the ledger holds the server's canonical row. The
 *     panel awaits that, disables its controls while it's in flight, and
 *     restores the prior value if it rejects.
 * The synchronous mock return is what keeps the fixture walkthrough landing in
 * the same tick it was clicked; nothing else depends on it.
 *
 * Update discipline: an ordinary update (approve, depth change, filter toggle)
 * replaces only the banner + row-list sub-containers and calls
 * `filterBar.setCount()`. The `ResultsFilterBar` instance persists across every
 * one of those; it is destroyed and rebuilt only by `PartnerSection.render()`,
 * which owns the surrounding markup.
 *
 * Panel ownership uses the shipped local-flag idiom (`PracticesSection` /
 * `PartnerNotesSection`): a `_panelOpen` + `_panelContentType` pair, a plain
 * `right-panel:close` emitted only while one of our own `sharing-*` types is
 * current, and a `right-panel:closed` listener that checks `data.contentType`.
 * No `RightPanelManager` API is added.
 *
 * @module sections/partnerSharing/SharingRegistry
 */

import { ResultsFilterBar } from '../../components/ResultsFilterBar.js';
import { escapeHtml } from '../../core/utils.js';
import {
  pillFor,
  needsReviewCount,
  gestaltGroupId,
  queueScope
} from './sharingLedgerModel.js';
import {
  SharingDetailPanel,
  SHARING_CONTENT_TYPES,
  renderSharingPill
} from './SharingDetailPanel.js';

/** The `contentType`s this surface owns on the shared right panel. */
const OWN_CONTENT_TYPES = new Set(SHARING_CONTENT_TYPES);

/** The filter menu's "no level filter" sentinel — deliberately not an option
 *  value, so the Sharing-level section renders uncheckmarked and the bar
 *  counts it as neutral. */
const LEVEL_NONE = 'none';

/** Chevron glyph, matching the shared `.note-row__chevron` in Notes/Practices rows. */
const CHEVRON_SVG = `
  <svg width="19" height="19" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  </svg>`;

export class SharingRegistry {
  /**
   * @param {Object} config
   * @param {HTMLElement} config.root - The `[data-sharing-registry]` shell rendered by `PartnerSection`
   * @param {Object} config.ledger - The PartnerSection-owned ledger (`{masters, groups, units}`)
   * @param {{emit: Function, on: Function, off: Function}} config.eventBus - App event bus
   * @param {string} [config.partnerName] - Display name interpolated into the locked copy
   * @param {boolean} [config.mockMode=false] - Fixture harness: every seam mutates
   *   locally and never touches the network
   * @param {{setUnitState: Function, submitSharing: Function}} [config.api] - Consent
   *   seams, supplied by `PartnerSection` (which owns the API client + the view model).
   *   `setUnitState(unitId, state)` resolves to the canonical unit row;
   *   `submitSharing(unitStates)` resolves once the host has re-synced the ledger
   *   from the batch response.
   */
  constructor({ root, ledger, eventBus, partnerName, mockMode = false, api = null }) {
    this.root = root;
    this.ledger = ledger;
    this.eventBus = eventBus;
    this.partnerName = partnerName || 'your partner';
    this.mockMode = !!mockMode;
    this.api = api;

    /** @type {ResultsFilterBar|null} Persists across every ordinary update. */
    this.filterBar = null;
    /** @type {{status: ('all'|'review'), level: ('none'|'full'|'basic'|'off')}}
     *  The filter menu's two radio sections. They behave as one single-select
     *  view (see {@link SharingRegistry#_handleFilterChange}). */
    this.filters = { status: 'all', level: LEVEL_NONE };
    /** @type {boolean} Whether one of OUR contentTypes currently owns the panel. */
    this._panelOpen = false;
    /** @type {string|null} */
    this._panelContentType = null;
    /** @type {string|null} Unit shown in the panel — drives the row's `aria-expanded`. */
    this._openUnitId = null;
    this._destroyed = false;

    this.panel = new SharingDetailPanel({ eventBus, host: this._panelHost() });

    this._onRootClick = this._onRootClick.bind(this);
    this._onRootKeydown = this._onRootKeydown.bind(this);
    this._onPanelClosed = this._onPanelClosed.bind(this);
    this._onPanelOpen = this._onPanelOpen.bind(this);

    this.root.addEventListener('click', this._onRootClick);
    this.root.addEventListener('keydown', this._onRootKeydown);
    this.eventBus.on('right-panel:closed', this._onPanelClosed);
    this.eventBus.on('right-panel:open', this._onPanelOpen);

    this.render();
  }

  /* ------------------------------------------------------------------ *
   * Ledger access + the three mutation seams (decision C)               *
   * ------------------------------------------------------------------ */

  /** @returns {Array<Object>} Every unit, in ledger order. */
  get units() {
    return this.ledger?.units || [];
  }

  /** @returns {Array<Object>} Every group, in ledger order. */
  get groups() {
    return this.ledger?.groups || [];
  }

  /**
   * Whether the "Share discovery content" master is off. Paused DIMS the
   * surface; it never disables it — masters gate crossing, not editing
   * (§3.2 gates-not-erasers), and the locked banner copy promises the choices
   * below are still saved.
   * @returns {boolean}
   */
  isPaused() {
    return this.ledger?.masters?.discovery === false;
  }

  /**
   * @param {string} unitId
   * @returns {Object|null}
   */
  _unit(unitId) {
    return this.units.find(u => u.id === unitId) || null;
  }

  /**
   * Commit a depth on an already-decided unit (immediate, no approve step).
   *
   * @param {string} unitId
   * @param {'full'|'basic'|'off'} state
   * @returns {Promise<void>|null} A promise on the real path (resolved once the
   *   unit holds the server's row), `null` under `mockMode`.
   */
  setUnitState(unitId, state) {
    const unit = this._unit(unitId);
    if (!unit) return null;
    if (this.mockMode) {
      unit.state = state;
      return null;
    }
    return this._patchUnit(unit, { state });
  }

  /**
   * Commit a never-reviewed unit's pre-selection — the R5 approve event.
   *
   * Also clears `revisionReverted`: the revision that pushed this unit back to
   * `Review` is exactly what the user just answered, so the "Updated — needs
   * another look" flag would otherwise stick forever. On the real path the
   * server's row governs that flag, as it governs everything else.
   *
   * @param {string} unitId
   * @param {'full'|'basic'|'off'} state
   * @returns {Promise<void>|null} As {@link SharingRegistry#setUnitState}
   */
  approveUnit(unitId, state) {
    const unit = this._unit(unitId);
    if (!unit) return null;
    if (this.mockMode) {
      unit.state = state;
      unit.revisionReverted = false;
      return null;
    }
    return this._patchUnit(unit, { state, revisionReverted: false });
  }

  /**
   * PATCH one unit and replace it from the canonical payload, IN PLACE — the
   * panel and the queue both hold this object by reference. A payload the
   * server didn't send falls back to the optimistic values the caller asked for.
   *
   * @param {Object} unit - The live ledger row
   * @param {Object} fallback - Fields to apply when no canonical row comes back
   * @returns {Promise<void>}
   */
  _patchUnit(unit, fallback) {
    return Promise.resolve(this.api.setUnitState(unit.id, fallback.state))
      .then(canonical => {
        Object.assign(unit, canonical && typeof canonical === 'object' ? canonical : fallback);
      });
  }

  /**
   * The overview's one write: a group's whole disclosure set — every unit's state
   * plus this member's confirm — committed atomically (R3 / R12). A later edit
   * to one of its units does NOT clear the seal: invalidation fires on register
   * revisions, modelled by `revisionReverted`, never on a user edit.
   *
   * The real path re-syncs the whole ledger from the submit response (the go-
   * live gate can flip every `state`, the partner's confirm, and the consumers
   * list at once), so it writes nothing locally.
   *
   * @param {string} groupId
   * @param {Record<string, 'full'|'basic'|'off'>} unitStates - Every unit in the group
   * @returns {Promise<void>|null} As {@link SharingRegistry#setUnitState}
   */
  submitGroup(groupId, unitStates) {
    const group = this.groups.find(g => g.id === groupId);
    if (!group) return null;
    if (this.mockMode) {
      Object.entries(unitStates || {}).forEach(([unitId, state]) => {
        const unit = this._unit(unitId);
        if (!unit) return;
        unit.state = state;
        // Approving is the answer to the revision that reverted the unit, so
        // the "needs another look" flag clears with it (mirrors approveUnit).
        unit.revisionReverted = false;
      });
      group.confirmed = true;
      return null;
    }
    return Promise.resolve(this.api.submitSharing(unitStates)).then(() => undefined);
  }

  /* ------------------------------------------------------------------ *
   * Rendering                                                           *
   * ------------------------------------------------------------------ */

  /** Full internal render: paused class, banner, rows, and a fresh filter bar. */
  render() {
    this.root.classList.toggle('partner-sharing-registry--paused', this.isPaused());
    this._renderBanners();
    this._renderRows();
    this._mountFilterBar();
  }

  /**
   * The ordinary-update path (approve, depth change, filter toggle, panel
   * open/close): banner + rows + the bar's count. The bar INSTANCE survives.
   */
  refresh() {
    if (this._destroyed) return;
    this._renderBanners();
    this._renderRows();
    this.filterBar?.setCount(this.visibleUnits().length);
  }

  /**
   * Whether the detail panel is mid-run over a staged, unsubmitted batch.
   * `PartnerSection` asks before a background poll re-renders the page: that
   * render destroys and rebuilds this registry, and the run with it.
   *
   * @returns {boolean}
   */
  hasActiveRun() {
    return this._panelOpen && this.panel.hasActiveRun();
  }

  /**
   * Open the queue run over one group's disclosure set — a thin public
   * passthrough to {@link SharingDetailPanel#openQueue}, for callers outside
   * this module (`PartnerSection`'s `?queue=<groupId>` deep-link intent —
   * decision D). The "Review all" click path (`_onRootClick`) keeps reaching
   * `this.panel.openQueue` directly; this is an additional entry point, not a
   * replacement for it.
   *
   * @param {string} groupId
   */
  openQueue(groupId) {
    this.panel.openQueue(groupId);
  }

  /**
   * The rows the current filter shows, review-first: undecided rows surface
   * at the top and decided rows keep ledger order below (a stable sort on the
   * two buckets). The resting order is the "needs your attention" order, so
   * the default page load leads with whatever still needs a first look.
   * @returns {Array<Object>}
   */
  visibleUnits() {
    const { status, level } = this.filters;
    let list = this.units;
    if (status === 'review') list = list.filter(u => u.state == null);
    if (level !== LEVEL_NONE) list = list.filter(u => u.state === level);
    return [...list].sort((a, b) => (a.state == null ? 0 : 1) - (b.state == null ? 0 : 1));
  }

  _renderBanners() {
    const mount = this.root.querySelector('[data-sharing-banners]');
    if (!mount) return;
    mount.innerHTML = this._pausedBannerHtml() + this._reviewBannerHtml();
  }

  /**
   * Shown whenever the "Share discovery content" master is off. THE BANNER
   * IS THE ONLY PAUSED SIGNAL (2026-08-25 round, reversing the earlier
   * dim-everything treatment): rows, pills, and the detail panel all stay at
   * full visual strength — they're fully live too (nothing below is
   * disabled), so dimming them read as a lie about their own interactivity.
   * Same white-card shell as the review banner below it (no caution-yellow
   * fill, no dot — bold amber title + a muted explanation line carry the
   * whole signal); "Manage in Overview" is a real `.btn--secondary.btn--small`
   * (a secondary action beside the review banner's primary CTA) instead of a
   * bare link.
   * @returns {string} HTML, or ''
   */
  _pausedBannerHtml() {
    if (!this.isPaused()) return '';
    return `
      <div class="sharing-banner sharing-banner--paused" role="status">
        <span class="sharing-banner__text">
          <strong>Partner Sharing Disabled</strong>
          <span class="sharing-banner__sub">Nothing is visible to ${escapeHtml(this.partnerName)}. Your choices below are still saved.</span>
        </span>
        <button type="button" class="btn btn--secondary btn--small sharing-banner__btn" data-action="partner-tab" data-tab="overview">Manage in Overview</button>
      </div>`;
  }

  /**
   * The queue's one entry point, above the filter bar: shown while the
   * gestalt group still has work — units awaiting a first look, or a decided
   * set this member hasn't approved yet (master off→on re-entry, single-mode
   * deciders). Once the group is confirmed and nothing needs review it
   * disappears entirely, "Review all" with it: edits to a live ledger are
   * single-row affairs, and the overview's "Approve & share" is the batch
   * confirm surface.
   *
   * One line — count lead + the "Review N" CTA — no reassurance sub-line
   * (dropped 2026-08-28, owner call: the count + button say enough on their
   * own). The CTA rides the shared `.btn--primary.btn--small` component
   * rather than page-local button styling, so it stays visually consistent
   * with every other small primary action in the app.
   * @returns {string} HTML, or ''
   */
  _reviewBannerHtml() {
    const groupId = gestaltGroupId(this.groups);
    if (!queueScope(this.units, groupId).length) return '';
    const needs = needsReviewCount(this.units);
    const group = this.groups.find(g => g.id === groupId);
    if (!needs && group?.confirmed) return '';
    const lead = needs
      ? `${needs} ${needs === 1 ? 'topic needs' : 'topics need'} your review`
      : 'Your choices are ready to approve';
    // "Review N" while something still needs a first look; "Review all" once
    // everything is decided and the button's only job is the final confirm
    // (needs === 0 but the group itself isn't confirmed yet).
    const buttonLabel = needs ? `Review ${needs} ${needs === 1 ? 'topic' : 'topics'}` : 'Review all';
    return `
      <div class="sharing-banner sharing-banner--review" role="status">
        <span class="sharing-banner__dot" aria-hidden="true"></span>
        <span class="sharing-banner__text">
          <strong>${lead}</strong>
        </span>
        <button type="button" class="btn btn--primary btn--small sharing-banner__btn" data-action="sharing-review-all">${escapeHtml(buttonLabel)}</button>
      </div>`;
  }

  /**
   * Repaint the row list. `animateIds` is the batch submit's one-shot set,
   * handed straight down its close call — it is never stored between paints,
   * so it can only ever be spent by the paint it was passed to. Every other
   * caller (filter toggle, single-unit edit, first mount, poll re-sync) passes
   * nothing and animates nothing.
   *
   * @param {Array<string>|null} [animateIds] - Unit ids whose pill should settle in
   */
  _renderRows(animateIds = null) {
    const mount = this.root.querySelector('[data-sharing-rows]');
    if (!mount) return;
    const animate = animateIds?.length ? new Set(animateIds) : null;
    const visible = this.visibleUnits();
    const emptyCopy = this.filters.status === 'review'
      ? 'Nothing needs review right now.'
      : 'No topics match this filter.';
    mount.innerHTML = visible.length
      ? visible.map(unit => this._rowHtml(unit, !!animate?.has(unit.id))).join('')
      : `<p class="partner-sharing-registry__empty">${emptyCopy}</p>`;
  }

  /**
   * One ledger row, on the shared `.card.session-card.note-row` idiom: name,
   * the state pill, and the hover chevron — a single line always (owner call
   * 2026-08-28: the nav card's one job is "does this need your review," not
   * readiness nuance; `subLineFor`'s notes still surface once you actually
   * open the unit, via the detail panel's `_readinessNoteHtml`). No row is
   * ever locked — every unit stays clickable, guide-consumed ones included.
   *
   * @param {Object} unit
   * @param {boolean} [animate=false] - Play the just-changed pill animation
   * @returns {string} HTML
   */
  _rowHtml(unit, animate = false) {
    const isOpen = this._panelOpen && this._openUnitId === unit.id;
    // Off rows carry a modifier that visually recedes them (nothing crosses),
    // so the list reads as "what's being shared" at a glance.
    const mods = `${unit.state === 'off' ? ' sharing-unit-row--off' : ''}${animate ? ' sharing-unit-row--changed' : ''}`;
    return `
      <div class="card session-card note-row sharing-unit-row${mods}"
           data-action="sharing-open-unit" data-unit-id="${escapeHtml(unit.id)}"
           role="button" tabindex="0" aria-expanded="${isOpen}">
        <div class="session-card__content">
          <div class="session-card__title-row">
            <h4 class="session-card__title sharing-unit-row__title">${escapeHtml(unit.name)}</h4>
          </div>
        </div>
        <span class="note-row__meta">
          <span class="note-row__status">${renderSharingPill(pillFor(unit))}</span>
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
        </span>
      </div>`;
  }

  /**
   * Mount (or re-mount) the standard list-page bar: a live count of the
   * visible rows ("N Topics") and one Filter menu holding two radio sections
   * — Status (All / Needs review) and Sharing level (Full / Basic / Off).
   * No standalone actions: "Review all" lives on the review banner above the
   * bar, and this surface has no select mode.
   */
  _mountFilterBar() {
    const mount = this.root.querySelector('[data-filter-bar-mount="partner-sharing"]');
    if (!mount) return;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    this.filterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Topic', plural: 'Topics' },
      count: this.visibleUnits().length,
      filters: [
        {
          id: 'status',
          label: 'Status',
          defaultValue: this.filters.status,
          neutralValue: 'all',
          options: [
            { value: 'all', label: 'All' },
            { value: 'review', label: 'Needs review' }
          ]
        },
        {
          id: 'level',
          label: 'Sharing level',
          defaultValue: this.filters.level,
          neutralValue: LEVEL_NONE,
          options: [
            { value: 'full', label: 'Full' },
            { value: 'basic', label: 'Basic' },
            { value: 'off', label: 'Off' }
          ]
        }
      ],
      onChange: state => this._handleFilterChange(state)
    });
  }

  /**
   * Apply a Filter-menu pick. The two sections behave as ONE single-select
   * view: a "needs review" row has no level, so the sections could only ever
   * intersect to an empty list — picking a level therefore resets a "Needs
   * review" pick and vice versa. `setFilterValue` syncs the bar's checkmarks
   * without re-entering this handler.
   * @param {{filters: {status: string, level: string}}} state
   */
  _handleFilterChange(state) {
    const next = { status: state.filters.status, level: state.filters.level };
    if (next.level !== this.filters.level && next.level !== LEVEL_NONE && next.status === 'review') {
      next.status = 'all';
      this.filterBar?.setFilterValue('status', 'all');
    } else if (next.status !== this.filters.status && next.status === 'review' && next.level !== LEVEL_NONE) {
      next.level = LEVEL_NONE;
      this.filterBar?.setFilterValue('level', LEVEL_NONE);
    }
    this.filters = next;
    this._renderRows();
    this.filterBar?.setCount(this.visibleUnits().length);
  }

  /* ------------------------------------------------------------------ *
   * Panel ownership                                                     *
   * ------------------------------------------------------------------ */

  /**
   * The `host` contract `SharingDetailPanel` reads and writes through.
   * @returns {Object}
   */
  _panelHost() {
    return {
      getLedger: () => this.ledger,
      getPartnerName: () => this.partnerName,
      setUnitState: (unitId, state) => this.setUnitState(unitId, state),
      approveUnit: (unitId, state) => this.approveUnit(unitId, state),
      submitGroup: (groupId, unitStates) => this.submitGroup(groupId, unitStates),
      onLedgerChanged: () => this.refresh(),
      onPanelOpened: (contentType, unitId) => {
        this._panelOpen = true;
        this._panelContentType = contentType;
        this._openUnitId = unitId || null;
        this._renderRows();
      },
      requestClose: (changedUnitIds) => this.closeOwnPanel(changedUnitIds)
    };
  }

  /**
   * Emit a plain `right-panel:close` — but only while WE own what's showing.
   * Mode state is released here rather than waiting for the `right-panel:closed`
   * round trip, so a stale mode can never be clicked in the gap.
   *
   * The rows are repainted HERE, explicitly: clearing the ownership flags first
   * is what makes `_onPanelClosed` early-return on our own close, so the
   * round-trip repaint never fires for a close we initiated — and the batch
   * submit's auto-close depends on that paint to land its row animation.
   *
   * @param {Array<string>} [changedUnitIds] - Passed only by the batch submit's
   *   auto-close; the rows it names settle their pill in on this paint.
   */
  closeOwnPanel(changedUnitIds = null) {
    if (!this._panelOpen || !OWN_CONTENT_TYPES.has(this._panelContentType)) return;
    this._clearPanelFlags();
    this.panel.handleClosed();
    if (!this._destroyed) this._renderRows(changedUnitIds);
    this.eventBus.emit('right-panel:close');
  }

  _clearPanelFlags() {
    this._panelOpen = false;
    this._panelContentType = null;
    this._openUnitId = null;
  }

  /**
   * Panel × / cross-detail close. Exact match against the CURRENT open
   * content type, not set membership: `right-panel:closed` fires only when
   * the slide transition settles, so a stale close from an earlier sharing
   * mode (a 'sharing-unit' close landing after the queue re-opened) must not
   * clear the ownership flags of the mode that superseded it. Another
   * section's panel closing never matches either.
   * @param {{contentType?: string}} data
   */
  _onPanelClosed(data) {
    if (!this._panelOpen || data?.contentType !== this._panelContentType) return;
    this._clearPanelFlags();
    this.panel.handleClosed();
    if (!this._destroyed) this._renderRows();
  }

  /**
   * Another surface took the panel over. Release ownership WITHOUT emitting a
   * close — the foreign content is now the panel's, and closing it here would
   * shut a panel we don't own.
   * @param {{contentType?: string}} data
   */
  _onPanelOpen(data) {
    if (OWN_CONTENT_TYPES.has(data?.contentType) || !this._panelOpen) return;
    this._clearPanelFlags();
    this.panel.handleClosed();
    if (!this._destroyed) this._renderRows();
  }

  /* ------------------------------------------------------------------ *
   * Interaction                                                         *
   * ------------------------------------------------------------------ */

  /**
   * Row clicks. The paused banner's "Manage in Overview" button deliberately
   * carries `data-action="partner-tab"` and is left to `PartnerSection`'s own
   * delegated handler — one pill-switch implementation, not two.
   * @param {MouseEvent} event
   */
  _onRootClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.root.contains(target)) return;
    const action = target.getAttribute('data-action');
    if (action === 'sharing-review-all') {
      event.preventDefault();
      this.panel.openQueue(gestaltGroupId(this.groups));
      return;
    }
    if (action === 'sharing-open-unit') {
      event.preventDefault();
      this.panel.openSingle(target.getAttribute('data-unit-id'));
    }
  }

  /**
   * Enter/Space on a row, guarding nested controls — the
   * `PartnerNotesSection.handleKeydown` pattern.
   * @param {KeyboardEvent} event
   */
  _onRootKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest('[data-action="sharing-open-unit"]');
    if (!row || !this.root.contains(row)) return;
    // A nested [data-action] owns the key itself (buttons activate natively).
    const inner = event.target.closest('[data-action]');
    if (inner && inner !== row) return;
    event.preventDefault();
    this.panel.openSingle(row.getAttribute('data-unit-id'));
  }

  /**
   * Release everything: the filter bar's document-level listeners, our two root
   * listeners, both event-bus subscriptions, and our own panel if it's open.
   * Called from `PartnerSection.render()` (pre-swap) and `deactivate()`.
   */
  destroy() {
    this.closeOwnPanel();
    this._destroyed = true;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    this.root.removeEventListener('click', this._onRootClick);
    this.root.removeEventListener('keydown', this._onRootKeydown);
    this.eventBus.off('right-panel:closed', this._onPanelClosed);
    this.eventBus.off('right-panel:open', this._onPanelOpen);
    this.panel.destroy();
  }
}
